import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, api } from '../App';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, ScatterChart, Scatter, Cell, PieChart, Pie,
  ComposedChart, Line, Legend
} from 'recharts';
import {
  Users, CheckCircle, TrendingUp, Target, LogOut, Settings, BookOpen,
  BarChart3, Leaf, Building2, AlertTriangle, RefreshCw, Activity,
  FileText, Sparkles, ArrowUp, ArrowDown, Copy
} from 'lucide-react';
import { PartnerLogos } from '../components/PartnerLogos';

const BRAND_LOGO_LIGHT = "/benchmark-ph-light.png";

// Colors for charts
const COLORS = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
const QUADRANT_COLORS = {
  high_high: '#10B981',
  high_low: '#F59E0B',
  low_high: '#3B82F6',
  low_low: '#94A3B8'
};

// Stat Card Component
function StatCard({ title, value, icon: Icon, trend, color = 'accent' }) {
  const colorClasses = {
    accent: 'bg-accent/10 text-accent',
    success: 'bg-emerald-100 text-emerald-600',
    warning: 'bg-amber-100 text-amber-600',
    primary: 'bg-slate-100 text-slate-600'
  };

  return (
    <div className="dashboard-card" data-testid={`stat-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-500 mb-1">{title}</p>
          <p className="text-3xl font-bold text-primary">{value}</p>
          {trend && (
            <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              {trend}
            </p>
          )}
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
}

// DEF-011: integer percentages that always sum to 100 (largest remainder method)
function withPercents(items) {
  const total = items.reduce((s, x) => s + x.value, 0);
  if (!total) return items.map(x => ({ ...x, pct: 0 }));
  const raw = items.map(x => (x.value / total) * 100);
  const floors = raw.map(Math.floor);
  let remainder = 100 - floors.reduce((a, b) => a + b, 0);
  const order = raw.map((r, i) => ({ i, frac: r - floors[i] })).sort((a, b) => b.frac - a.frac);
  const pcts = [...floors];
  for (let k = 0; k < remainder; k++) pcts[order[k].i] += 1;
  return items.map((x, i) => ({ ...x, pct: pcts[i] }));
}

// OverviewTab - Original analytics content
function OverviewTab({ analytics, timeline }) {
  const [segmentDrill, setSegmentDrill] = useState(false);
  const segmentData = analytics?.segment_distribution 
    ? withPercents(Object.entries(analytics.segment_distribution).map(([name, value]) => ({ name, value })))
    : [];
  const supportBreakdownData = analytics?.support_services_breakdown
    ? withPercents(Object.entries(analytics.support_services_breakdown).map(([name, value]) => ({ name, value })))
    : [];
  const materialityData = analytics?.materiality_matrix || [];

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard title="Total Respondents" value={analytics?.summary?.total_respondents || 0} icon={Users} color="accent" />
        <StatCard title="Completed Responses" value={analytics?.summary?.completed_responses || 0} icon={CheckCircle} color="success" />
        <StatCard title="Completion Rate" value={`${analytics?.summary?.completion_rate || 0}%`} icon={Target} color="warning" />
        <StatCard title="Has ESG Targets" value={`${analytics?.summary?.has_esg_targets_pct || 0}%`} icon={Leaf} color="primary" />
      </div>

      {/* Participation Momentum */}
      <div className="dashboard-card mb-8" data-testid="participation-momentum-chart">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-primary">Participation Momentum</h2>
            <p className="text-sm text-slate-500">Weekly invites sent vs. opens vs. completions — with cumulative completions trend</p>
          </div>
          <TrendingUp className="w-5 h-5 text-accent" />
        </div>
        {(timeline || []).length === 0 ? (
          <p className="text-slate-400 text-sm text-center py-10">No participation data yet</p>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={timeline} margin={{ top: 10, right: 30, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="week" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <RechartsTooltip
                formatter={(value, name) => [value, {
                  invited: 'Invites Sent',
                  opened: 'Opened',
                  completed: 'Completed',
                  cum_completed: 'Total Completed'
                }[name] || name]}
              />
              <Legend
                formatter={(name) => ({
                  invited: 'Invites Sent',
                  opened: 'Opened',
                  completed: 'Completed',
                  cum_completed: 'Total Completed (cumulative)'
                }[name] || name)}
              />
              <Bar dataKey="invited" fill="#94A3B8" radius={[4, 4, 0, 0]} barSize={16} />
              <Bar dataKey="opened" fill="#3B82F6" radius={[4, 4, 0, 0]} barSize={16} />
              <Bar dataKey="completed" fill="#40D39B" radius={[4, 4, 0, 0]} barSize={16} />
              <Line type="monotone" dataKey="cum_completed" stroke="#131B2E" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="dashboard-card col-span-1 lg:col-span-2" data-testid="materiality-matrix-chart">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-primary">Double Materiality Matrix</h2>
              <p className="text-sm text-slate-500">Financial vs Impact Relevance by ESG Issue</p>
            </div>
            <div className="flex gap-4 text-xs">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-emerald-500"></span>High Priority</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-amber-500"></span>Monitor</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={400}>
            <ScatterChart margin={{ top: 20, right: 30, bottom: 60, left: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis type="number" dataKey="financial" name="Financial Relevance" domain={[0, 5.5]} ticks={[0, 1, 2, 3, 4, 5]} label={{ value: 'Financial Relevance', position: 'bottom', offset: 40 }} />
              <YAxis type="number" dataKey="impact" name="Impact Relevance" domain={[0, 5.5]} ticks={[0, 1, 2, 3, 4, 5]} label={{ value: 'Impact Relevance', angle: -90, position: 'insideLeft', offset: -40 }} />
              <RechartsTooltip content={({ payload }) => {
                if (payload && payload.length) {
                  const data = payload[0].payload;
                  return (<div className="bg-white p-3 rounded-lg shadow-lg border text-sm"><p className="font-semibold text-primary">{data.name}</p><p className="text-slate-600">Financial: {data.financial}</p><p className="text-slate-600">Impact: {data.impact}</p></div>);
                }
                return null;
              }} />
              <Scatter data={materialityData} fill="#2563EB">
                {materialityData.map((entry, index) => {
                  const isHF = entry.financial >= 3, isHI = entry.impact >= 3;
                  let c = QUADRANT_COLORS.low_low;
                  if (isHF && isHI) c = QUADRANT_COLORS.high_high;
                  else if (isHF) c = QUADRANT_COLORS.high_low;
                  else if (isHI) c = QUADRANT_COLORS.low_high;
                  return <Cell key={`cell-${index}`} fill={c} />;
                })}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>

        <div className="dashboard-card" data-testid="segment-distribution-chart">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-primary">
              {segmentDrill ? 'Support Services Breakdown' : 'Business Segment Distribution'}
            </h2>
            {segmentDrill && (
              <button
                onClick={() => setSegmentDrill(false)}
                className="text-sm text-accent-dark hover:underline font-medium"
                data-testid="segment-drill-back-btn"
              >
                ← All segments
              </button>
            )}
          </div>
          {(segmentDrill ? supportBreakdownData : segmentData).length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={segmentDrill ? supportBreakdownData : segmentData}
                    cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value"
                    label={(entry) => `${entry.name} (${entry.pct}%)`}
                    onClick={(entry) => {
                      if (!segmentDrill && entry?.name === 'Support Services' && supportBreakdownData.length > 0) {
                        setSegmentDrill(true);
                      }
                    }}
                  >
                    {(segmentDrill ? supportBreakdownData : segmentData).map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                        cursor={!segmentDrill && entry.name === 'Support Services' ? 'pointer' : 'default'}
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
              {!segmentDrill && supportBreakdownData.length > 0 && segmentData.some(s => s.name === 'Support Services') && (
                <p className="text-xs text-slate-400 text-center mt-2" data-testid="segment-drill-hint">
                  Click the "Support Services" slice to see its breakdown
                </p>
              )}
            </>
          ) : (
            <div className="h-64 flex items-center justify-center text-slate-400">
              {segmentDrill ? 'No Support Services selections yet' : 'No data available yet'}
            </div>
          )}
        </div>

        <div className="dashboard-card" data-testid="kpi-summary">
          <h2 className="text-lg font-semibold text-primary mb-6">Industry KPI Averages</h2>
          <div className="space-y-4">
            {[
              { label: 'Avg. Attrition Rate', value: `${analytics?.kpi_summaries?.avg_attrition_rate || 0}%`, icon: Users },
              { label: 'Avg. Renewable Energy', value: `${analytics?.kpi_summaries?.avg_renewable_energy || 0}%`, icon: Leaf },
              { label: 'Avg. Training Hours', value: `${analytics?.kpi_summaries?.avg_training_hours || 0}h`, icon: Target },
              { label: 'Avg. Local Hiring', value: `${analytics?.kpi_summaries?.avg_local_hiring || 0}%`, icon: Building2 },
              { label: 'Total Data Breaches', value: analytics?.kpi_summaries?.total_data_breaches || 0, icon: AlertTriangle },
            ].map((kpi, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3"><kpi.icon className="w-5 h-5 text-slate-400" /><span className="text-sm text-slate-600">{kpi.label}</span></div>
                <span className="font-semibold text-primary">{kpi.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="dashboard-card" data-testid="materiality-details">
        <h2 className="text-lg font-semibold text-primary mb-6">Materiality Details</h2>
        <div className="overflow-x-auto">
          <table className="admin-table">
            <thead><tr><th>ESG Issue</th><th>Financial Relevance</th><th>Impact Relevance</th><th>Priority</th></tr></thead>
            <tbody>
              {materialityData.map((item, idx) => {
                const isHF = item.financial >= 3, isHI = item.impact >= 3;
                let priority = 'Low', pColor = 'text-slate-500';
                if (isHF && isHI) { priority = 'High'; pColor = 'text-emerald-600'; }
                else if (isHF || isHI) { priority = 'Medium'; pColor = 'text-amber-600'; }
                return (
                  <tr key={idx}>
                    <td className="font-medium">{item.name}</td>
                    <td><div className="flex items-center gap-2"><div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-accent rounded-full" style={{ width: `${(item.financial / 5) * 100}%` }} /></div><span className="text-sm">{item.financial}</span></div></td>
                    <td><div className="flex items-center gap-2"><div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(item.impact / 5) * 100}%` }} /></div><span className="text-sm">{item.impact}</span></div></td>
                    <td className={`font-medium ${pColor}`}>{priority}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// KPI Analysis Tab
function KpiStatCard({ stat }) {
  const Icon = stat.lower_better ? ArrowDown : ArrowUp;
  const goodColor = stat.lower_better ? 'text-emerald-600' : 'text-emerald-600';
  return (
    <div className="dashboard-card" data-testid={`kpi-stat-${stat.key}`}>
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-sm font-medium text-slate-500">{stat.label}</h3>
        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{stat.unit}</span>
      </div>
      <p className="text-2xl font-bold text-primary mb-2">{stat.avg}</p>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="text-center p-2 bg-slate-50 rounded-lg">
          <p className="text-slate-400">Min</p>
          <p className="font-semibold text-slate-700">{stat.min}</p>
        </div>
        <div className="text-center p-2 bg-slate-50 rounded-lg">
          <p className="text-slate-400">Median</p>
          <p className="font-semibold text-slate-700">{stat.median}</p>
        </div>
        <div className="text-center p-2 bg-slate-50 rounded-lg">
          <p className="text-slate-400">Max</p>
          <p className="font-semibold text-slate-700">{stat.max}</p>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-1 text-xs text-slate-400">
        <Icon className={`w-3 h-3 ${goodColor}`} />
        <span>{stat.lower_better ? 'Lower is better' : 'Higher is better'}</span>
        <span className="ml-auto">{stat.count} responses</span>
      </div>
    </div>
  );
}

function NarrativeSection({ narrative, loading, onGenerate }) {
  const formatNarrative = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
      if (line.startsWith('## ')) return <h2 key={i} className="text-lg font-bold text-primary mt-6 mb-3">{line.replace('## ', '')}</h2>;
      if (line.startsWith('### ')) return <h3 key={i} className="text-base font-semibold text-slate-700 mt-4 mb-2">{line.replace('### ', '')}</h3>;
      if (line.startsWith('**') && line.endsWith('**')) return <p key={i} className="font-semibold text-slate-800 mt-2">{line.replace(/\*\*/g, '')}</p>;
      if (line.startsWith('- ') || line.startsWith('* ')) return <li key={i} className="ml-4 text-slate-600 text-sm leading-relaxed list-disc">{line.replace(/^[-*] /, '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').split(/(<strong>.*?<\/strong>)/).map((part, j) => part.startsWith('<strong>') ? <strong key={j}>{part.replace(/<\/?strong>/g, '')}</strong> : part)}</li>;
      if (line.match(/^\d+\./)) return <li key={i} className="ml-4 text-slate-600 text-sm leading-relaxed list-decimal">{line.replace(/^\d+\.\s*/, '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').split(/(<strong>.*?<\/strong>)/).map((part, j) => part.startsWith('<strong>') ? <strong key={j}>{part.replace(/<\/?strong>/g, '')}</strong> : part)}</li>;
      if (line.trim() === '') return <div key={i} className="h-2" />;
      return <p key={i} className="text-slate-600 text-sm leading-relaxed">{line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').split(/(<strong>.*?<\/strong>)/).map((part, j) => part.startsWith('<strong>') ? <strong key={j}>{part.replace(/<\/?strong>/g, '')}</strong> : part)}</p>;
    });
  };

  return (
    <div className="dashboard-card" data-testid="ai-narrative-section">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-primary">AI Narrative Assistant</h2>
            <p className="text-xs text-slate-500">Powered by Gemini 3 Flash LLM</p>
          </div>
        </div>
        <button
          onClick={onGenerate}
          disabled={loading}
          className="btn-accent h-10 px-5 flex items-center gap-2 text-sm"
          data-testid="generate-narrative-btn"
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <FileText className="w-4 h-4" />
              {narrative ? 'Regenerate' : 'Generate Narrative'}
            </>
          )}
        </button>
      </div>

      {!narrative && !loading && (
        <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl">
          <Sparkles className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 font-medium">AI-Powered KPI Analysis</p>
          <p className="text-sm text-slate-400 mt-1 max-w-md mx-auto">
            Click "Generate Narrative" to get an AI-written executive summary with insights and strategic recommendations based on all respondent KPI data.
          </p>
        </div>
      )}

      {loading && (
        <div className="text-center py-12">
          <div className="w-10 h-10 border-3 border-accent/30 border-t-accent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Analyzing KPI data across all respondents...</p>
          <p className="text-xs text-slate-400 mt-1">This may take a moment</p>
        </div>
      )}

      {narrative && !loading && (
        <div className="prose prose-sm max-w-none bg-slate-50 rounded-xl p-6 border border-slate-100">
          <div className="flex justify-end mb-2">
            <button 
              onClick={() => { navigator.clipboard.writeText(narrative); toast.success('Narrative copied!'); }}
              className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1"
              data-testid="copy-narrative-btn"
            >
              <Copy className="w-3 h-3" /> Copy
            </button>
          </div>
          {formatNarrative(narrative)}
        </div>
      )}
    </div>
  );
}

function KpiAnalysisTab({ kpiData, narrative, narrativeLoading, onGenerateNarrative }) {
  if (!kpiData) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent"></div>
      </div>
    );
  }

  const envStats = kpiData.kpi_stats.filter(s => ['renewable_energy_pct', 'carbon_emissions', 'ewaste_recycled_pct', 'electricity_kwh'].includes(s.key));
  const socialStats = kpiData.kpi_stats.filter(s => ['attrition_rate', 'training_hours', 'local_hiring_pct', 'living_wage_pct', 'community_investment'].includes(s.key));
  const govStats = kpiData.kpi_stats.filter(s => ['data_breaches', 'ai_automation_pct'].includes(s.key));

  const distColors = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  return (
    <>
      {/* Header stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="dashboard-card bg-gradient-to-br from-emerald-50 to-white border-emerald-100">
          <div className="flex items-center gap-3 mb-1">
            <Leaf className="w-5 h-5 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-700">Environmental KPIs</span>
          </div>
          <p className="text-2xl font-bold text-emerald-800">{envStats.length}</p>
          <p className="text-xs text-emerald-600">{kpiData.total_completed} respondents analyzed</p>
        </div>
        <div className="dashboard-card bg-gradient-to-br from-blue-50 to-white border-blue-100">
          <div className="flex items-center gap-3 mb-1">
            <Users className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-700">Social KPIs</span>
          </div>
          <p className="text-2xl font-bold text-blue-800">{socialStats.length}</p>
          <p className="text-xs text-blue-600">{kpiData.total_completed} respondents analyzed</p>
        </div>
        <div className="dashboard-card bg-gradient-to-br from-violet-50 to-white border-violet-100">
          <div className="flex items-center gap-3 mb-1">
            <Target className="w-5 h-5 text-violet-600" />
            <span className="text-sm font-medium text-violet-700">Governance KPIs</span>
          </div>
          <p className="text-2xl font-bold text-violet-800">{govStats.length}</p>
          <p className="text-xs text-violet-600">{kpiData.total_completed} respondents analyzed</p>
        </div>
      </div>

      {/* Environmental KPIs */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-2 h-6 bg-emerald-500 rounded-full" />
          <h2 className="text-lg font-semibold text-primary">Environmental Performance</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {envStats.map(stat => <KpiStatCard key={stat.key} stat={stat} />)}
        </div>
      </div>

      {/* Social KPIs */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-2 h-6 bg-blue-500 rounded-full" />
          <h2 className="text-lg font-semibold text-primary">Social Performance</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {socialStats.map(stat => <KpiStatCard key={stat.key} stat={stat} />)}
        </div>
      </div>

      {/* Governance KPIs */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-2 h-6 bg-violet-500 rounded-full" />
          <h2 className="text-lg font-semibold text-primary">Governance Performance</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {govStats.map(stat => <KpiStatCard key={stat.key} stat={stat} />)}
          {/* Privacy Policy Distribution */}
          {kpiData.distributions.privacy_policy && (
            <div className="dashboard-card" data-testid="privacy-policy-dist">
              <h3 className="text-sm font-medium text-slate-500 mb-3">Privacy Policy Adoption</h3>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={kpiData.distributions.privacy_policy} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="count" label={({ range, count }) => `${range}: ${count}`}>
                    <Cell fill="#10B981" />
                    <Cell fill="#EF4444" />
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Distribution Charts */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-2 h-6 bg-amber-500 rounded-full" />
          <h2 className="text-lg font-semibold text-primary">KPI Distributions</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Object.entries(kpiData.distributions)
            .filter(([key]) => key !== 'privacy_policy')
            .map(([key, dist]) => {
              const labels = { attrition_rate: 'Attrition Rate', renewable_energy_pct: 'Renewable Energy', training_hours: 'Training Hours', living_wage_pct: 'Living Wage Compliance', ewaste_recycled_pct: 'E-Waste Recycled' };
              return (
                <div key={key} className="dashboard-card" data-testid={`dist-chart-${key}`}>
                  <h3 className="text-sm font-semibold text-primary mb-4">{labels[key] || key} Distribution</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={dist} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="range" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <RechartsTooltip />
                      <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                        {dist.map((_, i) => <Cell key={i} fill={distColors[i % distColors.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              );
            })}
        </div>
      </div>

      {/* Comprehensive Stats Table */}
      <div className="dashboard-card mb-8" data-testid="kpi-stats-table">
        <h2 className="text-lg font-semibold text-primary mb-6">Comprehensive KPI Summary</h2>
        <div className="overflow-x-auto">
          <table className="admin-table">
            <thead>
              <tr>
                <th>KPI Metric</th>
                <th>Average</th>
                <th>Median</th>
                <th>Min</th>
                <th>Max</th>
                <th>Responses</th>
                <th>Direction</th>
              </tr>
            </thead>
            <tbody>
              {kpiData.kpi_stats.map((stat, idx) => (
                <tr key={idx}>
                  <td className="font-medium">{stat.label}</td>
                  <td className="font-semibold text-primary">{stat.avg} {stat.unit !== 'count' && stat.unit !== 'PHP' && stat.unit !== 'kWh' && stat.unit !== 'tCO2e' ? stat.unit : ''}</td>
                  <td>{stat.median}</td>
                  <td className="text-slate-500">{stat.min}</td>
                  <td className="text-slate-500">{stat.max}</td>
                  <td>{stat.count}</td>
                  <td>
                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${stat.lower_better ? 'text-blue-600' : 'text-emerald-600'}`}>
                      {stat.lower_better ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />}
                      {stat.lower_better ? 'Lower better' : 'Higher better'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* AI Narrative */}
      <NarrativeSection 
        narrative={narrative} 
        loading={narrativeLoading} 
        onGenerate={onGenerateNarrative} 
      />
    </>
  );
}

export default function AdminDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [kpiData, setKpiData] = useState(null);
  const [narrative, setNarrative] = useState('');
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const { logout } = useAuth();

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const [res, tl] = await Promise.all([
        api.get('/api/admin/analytics'),
        api.get('/api/admin/participation-timeline')
      ]);
      setAnalytics(res.data);
      setTimeline(tl.data.timeline || []);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const loadKpiData = useCallback(async () => {
    if (kpiData) return;
    try {
      const res = await api.get('/api/admin/kpi-analysis');
      setKpiData(res.data);
    } catch (error) {
      toast.error('Failed to load KPI data');
    }
  }, [kpiData]);

  useEffect(() => {
    if (activeTab === 'kpi') loadKpiData();
  }, [activeTab, loadKpiData]);

  const generateNarrative = async () => {
    setNarrativeLoading(true);
    try {
      const res = await api.post('/api/admin/kpi-narrative', {});
      setNarrative(res.data.narrative);
    } catch (error) {
      toast.error('Failed to generate narrative');
    } finally {
      setNarrativeLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={BRAND_LOGO_LIGHT} alt="Benchmark PH" className="h-10 object-contain" />
            <div>
              <h1 className="font-semibold text-primary">Admin Dashboard</h1>
              <p className="text-xs text-slate-500">ESG Analytics Overview</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin/telemetry"
              className="btn-secondary h-10 px-4 flex items-center gap-2 text-sm"
              data-testid="telemetry-link"
            >
              <Activity className="w-4 h-4" />
              Telemetry
            </Link>
            <Link
              to="/admin/guide"
              className="text-slate-500 hover:text-primary p-2"
              title="Administrator Guide"
              data-testid="admin-guide-link"
            >
              <BookOpen className="w-5 h-5" />
            </Link>
            <Link
              to="/admin/console"
              className="btn-secondary h-10 px-4 flex items-center gap-2 text-sm"
              data-testid="console-link"
            >
              <Settings className="w-4 h-4" />
              Console
            </Link>
            <button
              onClick={loadAnalytics}
              className="p-2 text-slate-500 hover:text-primary transition-colors"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <button
              onClick={logout}
              className="text-slate-500 hover:text-slate-700 p-2"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="px-4 md:px-8 flex gap-1 -mb-px">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-all ${
              activeTab === 'overview'
                ? 'border-accent text-accent'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
            data-testid="tab-overview"
          >
            <BarChart3 className="w-4 h-4 inline mr-2" />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('kpi')}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-all ${
              activeTab === 'kpi'
                ? 'border-accent text-accent'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
            data-testid="tab-kpi-analysis"
          >
            <Activity className="w-4 h-4 inline mr-2" />
            KPI Analysis
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 md:px-8 py-8">
        {activeTab === 'overview' && <OverviewTab analytics={analytics} timeline={timeline} />}
        {activeTab === 'kpi' && (
          <KpiAnalysisTab 
            kpiData={kpiData} 
            narrative={narrative}
            narrativeLoading={narrativeLoading}
            onGenerateNarrative={generateNarrative}
          />
        )}
      </main>

      {/* Footer */}
      <footer className="px-4 py-6 text-center text-slate-500 text-sm border-t border-slate-200 bg-white">
        <PartnerLogos className="mb-3" />
        Developed for <span className="text-slate-600">IBPAP</span> by{' '}
        <a 
          href="https://sustek.ai/solutions/soctech" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-emerald-600 hover:text-emerald-700 hover:underline"
        >
          SusTek SocTech Labs
        </a>
      </footer>
    </div>
  );
}
